// Pure reconcile logic for the night screen's optimistic action submits
// (audit P2).
//
// The server records night actions silently and the only feedback channel is
// the verb (re)appearing in `view.myPendingVerbs`. The naive diff — "any verb
// I believed submitted that is absent from the latest broadcast was rejected"
// — raced interleaved broadcasts: a view projected BEFORE the server
// processed the just-sent action (someone else acted, chat, a reconnect)
// arrives without the verb and used to flash a false «НЕ ПРИНЯТО».
//
// Fix: track each submit as in-flight with its send time. While the ack
// grace window is open, absence from server truth keeps the optimistic lock
// and is NOT a rejection; the verb is only declared rejected once the window
// closes without the server ever echoing it (or when a previously-ACKED verb
// vanishes — the server really dropped it, e.g. the target became invalid).

import type { ActionVerb } from '../../shared/src/types.js';

// How long an optimistic submit may stay unacked before its absence from a
// broadcast counts as a silent rejection. Round-trips on the LAN this game
// targets are tens of ms; 3s absorbs a slow phone radio without leaving a
// genuinely-rejected action stuck for long.
export const SUBMIT_GRACE_MS = 3000;

export interface ReconcileResult {
  /** Verbs the UI should render as submitted/locked: server truth plus
   *  still-in-flight optimistic submits. */
  locked: Set<ActionVerb>;
  /** Verbs that are now known rejected — flash «НЕ ПРИНЯТО» for these. */
  rejected: ActionVerb[];
  /** In-flight submits still awaiting their ack (acked/expired removed). */
  inflight: Map<ActionVerb, number>;
}

export function reconcileSubmits(
  serverVerbs: readonly ActionVerb[],
  inflight: ReadonlyMap<ActionVerb, number>,
  prevLocked: ReadonlySet<ActionVerb>,
  now: number,
  graceMs: number = SUBMIT_GRACE_MS,
): ReconcileResult {
  const server = new Set(serverVerbs);
  // Ack in-flight submits the server echoed; keep the rest optimistic while
  // their grace window is open.
  const nextInflight = new Map<ActionVerb, number>();
  for (const [verb, sentAt] of inflight) {
    if (server.has(verb)) continue; // acked — server truth carries it now
    if (now - sentAt < graceMs) nextInflight.set(verb, sentAt);
    // else: expired unacked — falls through to the rejection diff below
  }
  const locked = new Set<ActionVerb>(server);
  for (const verb of nextInflight.keys()) locked.add(verb);
  // Anything previously locked that neither server truth nor an open grace
  // window still covers was silently rejected.
  const rejected: ActionVerb[] = [];
  for (const verb of prevLocked) {
    if (!locked.has(verb)) rejected.push(verb);
  }
  return { locked, rejected, inflight: nextInflight };
}
